import React, { useState } from "react";
import { Pie } from "react-chartjs-2";
import { Link } from "react-router-dom";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Helmet } from "react-helmet";

ChartJS.register(ArcElement, Tooltip, Legend);

const PnbSipCalculator = ({ isSip, setIsSip }) => {
  const [monthlyInvestment, setMonthlyInvestment] = useState(25000);
  const [expectedReturnRate, setExpectedReturnRate] = useState(12);
  const [timePeriod, setTimePeriod] = useState(10);

  const calculateSIP = () => {
    const monthlyRate = expectedReturnRate / 100 / 12;
    const months = timePeriod * 12;
    const investedAmount = monthlyInvestment * months;
    const futureValue =
      monthlyInvestment *
      ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) *
      (1 + monthlyRate);
    const estimatedReturns = futureValue - investedAmount;

    return {
      investedAmount,
      estimatedReturns,
      totalValue: futureValue,
    };
  };

  const { investedAmount, estimatedReturns, totalValue } = calculateSIP();

  const data = {
    labels: ["Invested Amount", "Estimated Returns"],
    datasets: [
      {
        label: "SIP Returns Breakdown",
        data: [investedAmount, estimatedReturns],
        backgroundColor: ["#36A2EB", "#FF6384"],
      },
    ],
  };
  function Icon({ id, open }) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        className={`${
          id === open ? "rotate-180" : ""
        } h-5 w-5 transition-transform`}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
        />
      </svg>
    );
  }

  const [activeIndex, setActiveIndex] = useState(0);

  const faqs = [
    {
        question: "Is the PNB SIP Calculator free to use on TheFinmaster?",
        answer: "Yes, it’s absolutely free with no hidden charges or sign-ups required.",
      },
      {
        question: "Is the calculator limited to PNB Mutual Funds?",
        answer: "It’s optimized for PNB Mutual Funds but can simulate SIPs from any AMC using the same logic.",
      },
      {
        question: "Can I use this calculator for lump-sum investments?",
        answer: "No, this tool is specifically for SIP (recurring) investments.",
      },
      {
        question: "How accurate are the maturity projections?",
        answer: "The results are estimates based on assumed return rates and do not guarantee future performance.",
      },
      {
        question: "Can I change the SIP amount or return rate during calculation?",
        answer: "Yes, the calculator is fully dynamic and lets you tweak all variables in real-time.",
      },
      {
        question: "Does the calculator consider taxes?",
        answer: "No, it provides gross estimates. Post-tax returns may vary depending on your fund type and holding period.",
      },
      {
        question: "Can I compare different SIP plans side-by-side?",
        answer: "You can manually simulate multiple scenarios to compare them effectively.",
      },
      {
        question: "Is it suitable for beginners?",
        answer: "Yes. It’s designed to be intuitive and educational for first-time investors.",
      },
      {
        question: "Does it recommend which PNB Mutual Fund to choose?",
        answer: "No. It’s a calculator, not a recommendation engine. Use it to plan once you’ve shortlisted funds.",
      },
      {
        question: "Does the calculator require KYC details?",
        answer: "No personal or KYC details are required to use this tool.",
      },
      {
        question: "Can I calculate SIPs for 30 years or more?",
        answer: "Yes, the tool supports long-term planning up to 40 years.",
      },
      {
        question: "Can I download or share the result?",
        answer: "Yes, TheFinmaster allows you to screenshot or export results for reference.",
      },
      {
        question: "Can I use the calculator on mobile?",
        answer: "Yes. It’s mobile-responsive and easy to use across all devices.",
      },
      {
        question: "Is SIP in PNB Mutual Funds safe?",
        answer: "While market-linked, PNB Mutual Funds are regulated by SEBI and professionally managed.",
      },
      {
        question: "What should I do after calculating my SIP?",
        answer: "Choose the fund, complete your KYC, and set up a SIP through your bank or mutual fund platform.",
      }
    ]
  const toggleAccordion = (index) => {
    if (activeIndex === index) {
      // If the clicked question is already expanded, collapse it
      setActiveIndex(null);
    } else {
      // If the clicked question is not expanded, expand it
      setActiveIndex(index);
    }
  };
  return (
    <>
      <Helmet>
        <title>
          PNB Bank SIP Calculator- Calculate Returns on PNB Bank SIP Investments |
          TheFinmaster
        </title>
        {/* <meta
          name="description"
          content="Free SIP Calculator! Estimate your returns on Mutual Fund investments with Systematic Investment Plans."
        />
        <meta
          name="keywords"
          content="sip return rate, monthly sip calculator, step up sip, sip calculator online"
        />
        <link
          rel="canonical"
          href="https://thefinmaster.com/financial-tools-calculators/sip-calculator"
        /> */}
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>

      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800 ">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <Link
              to="/financial-tools-calculators"
              className="text-blue-600 hover:text-blue-800"
            >
              Financial Tools Calculators
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500 "> PNB SIP Calculator</span>
          </li>
        </ol>
      </nav>
      <div className="py-6">
        <div className="container mx-auto p-4 shadow-xl rounded-md ">
          <h1 className="text-2xl font-bold mb-4">
          PNB Bank SIP Returns Calculator
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Monthly Investment
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="100000"
                    value={monthlyInvestment}
                    onChange={(e) =>
                      setMonthlyInvestment(Number(e.target.value))
                    }
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">
                    ₹ {monthlyInvestment.toLocaleString()}
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100000"
                  value={monthlyInvestment}
                  onChange={(e) => setMonthlyInvestment(Number(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Expected Return Rate (P.A.)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={expectedReturnRate}
                    onChange={(e) =>
                      setExpectedReturnRate(Number(e.target.value))
                    }
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{expectedReturnRate} %</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="0.1"
                  value={expectedReturnRate}
                  onChange={(e) =>
                    setExpectedReturnRate(Number(e.target.value))
                  }
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Time Period (Years)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={timePeriod}
                    onChange={(e) => setTimePeriod(Number(e.target.value))}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{timePeriod} Yr</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="50"
                  value={timePeriod}
                  onChange={(e) => setTimePeriod(Number(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <button
                onClick={calculateSIP}
                className="bg-blue-500 text-white px-4 py-2 rounded"
              >
                Calculate
              </button>
            </div>
            <div>
              <div className="mb-4">
                <h2 className="text-xl font-bold">Results</h2>
                <p>Invested Amount: ₹ {investedAmount.toLocaleString()}</p>
                <p>Estimated Returns: ₹ {estimatedReturns.toLocaleString()}</p>
                <p>Total Value: ₹ {totalValue.toLocaleString()}</p>
              </div>
              <div>
                <h2 className="text-xl font-bold">Breakdown</h2>
                <div className="flex justify-center">
                  <Pie
                    data={data}
                    options={{ maintainAspectRatio: false }}
                    width={300}
                    height={300}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6">
      <div className="w-full md:w-2/3 p-4 mx-auto">
      <div className="container mx-auto py-4">
          <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
            What is the PNB SIP Calculator?
          </h1>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            The PNB SIP Calculator is a digital financial tool that allows you to estimate the future value of your monthly investments made through a Systematic Investment Plan (SIP) in PNB Mutual Funds. By inputting basic details such as your monthly SIP amount, duration, and expected return, the calculator instantly provides you with the projected maturity amount, total invested capital, and wealth gained.
          </p>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            PNB Mutual Fund, promoted by Punjab National Bank—one of India’s oldest and most trusted public sector banks—offers a variety of SIP plans tailored to meet diverse financial objectives. Whether you're saving for retirement, buying a home, or funding your child’s education, this calculator helps you simulate investment scenarios and build a structured approach to wealth creation.
          </p>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            It’s a must-use tool for anyone considering SIPs with PNB Mutual Fund, enabling smart, informed, and goal-oriented investing.
          </p>
</div>
          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              What is SIP and Why Is It Popular?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              A Systematic Investment Plan (SIP) is a disciplined, automated method of investing in mutual funds. Rather than investing a lump sum all at once, SIP allows you to invest a fixed amount at regular intervals—usually monthly—thereby spreading out risk and building wealth gradually.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Why SIPs are so popular:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Budget-Friendly:</strong> You can start investing with as little as ₹500 per month.</li>
              <li><strong>Rupee Cost Averaging:</strong> In volatile markets, SIP helps you buy more units when prices are low and fewer when they’re high—averaging your cost over time.</li>
              <li><strong>Compounding Power:</strong> The longer you stay invested, the more your investments grow by earning returns on both capital and accumulated returns.</li>
              <li><strong>Disciplined Approach:</strong> SIPs promote financial discipline by creating a habit of regular investing.</li>
              <li><strong>Convenience:</strong> Auto-debit features mean your investment happens on time without the need for manual tracking.</li>
              <li><strong>Emotional Detachment:</strong> Removes the stress of market timing, allowing you to stay invested for the long term.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              These benefits make SIPs ideal for beginners and seasoned investors alike, and a SIP calculator helps bring clarity and predictability to this powerful investment tool.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How Can a PNB SIP Calculator Help You?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The PNB SIP Calculator acts as a planning assistant that simplifies complex financial math and shows you what your future wealth could look like based on your SIPs.
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Here’s how it helps:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Goal Alignment:</strong> Whether it’s ₹10 lakhs for your daughter’s education or ₹25 lakhs for a down payment on a house, you can determine the SIP amount needed to meet that goal.</li>
              <li><strong>Visualization of Growth:</strong> See a breakdown of your total investment vs. your returns, represented with graphs and tables.</li>
              <li><strong>Custom Scenarios:</strong> Adjust variables like SIP amount, time frame, or return rate to explore different investment strategies.</li>
              <li><strong>Real-Time Estimates:</strong> Get instant projections without needing spreadsheets or financial advisors.</li>
              <li><strong>Efficient Financial Planning:</strong> Helps you plan long-term goals by showing how consistent, small savings can snowball into significant wealth.</li>
              <li><strong>Comparison Tool:</strong> Try different PNB Mutual Fund options and compare which one might align best with your risk tolerance and goal horizon.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The calculator removes guesswork and equips you with actionable data to plan your financial future with precision.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How Does the PNB Mutual Fund SIP Calculator Work?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The PNB SIP Calculator uses a standard financial formula based on compounding to estimate future value:
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Formula Used:</strong><br />
              Future Value = P × [(1 + r)^n – 1] × (1 + r) / r
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Where:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>P = SIP amount per month</li>
              <li>r = expected monthly return (annual return ÷ 12)</li>
              <li>n = total investment months (years × 12)</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Example:</strong><br />
              Let’s say you invest ₹3,000 monthly for 15 years at an expected return of 11% per annum:<br />
              r = 11 ÷ 12 ÷ 100 = 0.00917<br />
              n = 15 × 12 = 180 months<br />
              Plugging these into the formula, the calculator estimates a future value of over ₹15.6 lakhs, with only ₹5.4 lakhs invested.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The calculator simplifies this entire math into an instant projection—showing your maturity amount, total invested capital, and gains—all in a user-friendly interface.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How to Use the PNB SIP Calculator Online
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Using the PNB SIP Calculator on TheFinmaster is simple and requires no prior financial expertise.
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Step-by-Step Instructions:</strong>
            </p>
            <ol className="list-decimal pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Input SIP Amount: Enter how much you plan to invest every month.</li>
              <li>Set Investment Duration: Choose the number of years or months you plan to stay invested.</li>
              <li>Select Expected Return Rate: Based on past performance or fund type, set an annual return rate (e.g., 8% for debt, 10–15% for equity).</li>
              <li>Click on 'Calculate': Instantly view:<br />
                - Maturity amount<br />
                - Total invested amount<br />
                - Estimated returns
              </li>
              <li>Analyze Graphs: Get visual insights into how your wealth compounds over time.</li>
            </ol>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Tips:</strong><br />
              Run simulations with different durations or SIP amounts to see what suits your goals.<br />
              Adjust return assumptions to reflect conservative or aggressive strategies.<br />
              Bookmark or download results to track and revisit later.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Advantages of Using TheFinmaster PNB SIP Calculator
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The PNB SIP Calculator by TheFinmaster is designed for both beginners and experienced investors. Its value goes beyond just number crunching.
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Key Advantages:</strong>
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Fast & Free:</strong> Get real-time, accurate estimates within seconds—at zero cost.</li>
              <li><strong>Simple Interface:</strong> Intuitive design for anyone to use, regardless of financial background.</li>
              <li><strong>Visual Breakdown:</strong> Clear graphs showing capital vs. returns for easy comprehension.</li>
              <li><strong>Goal-Oriented Planning:</strong> Allows you to reverse-calculate SIPs based on a desired financial goal.</li>
              <li><strong>No Login Required:</strong> Use the calculator instantly without creating an account.</li>
              <li><strong>Customizable:</strong> Try different investment amounts, timeframes, and return rates to suit your personal financial strategy.</li>
              <li><strong>Trustworthy Source:</strong> Hosted on TheFinmaster, a platform dedicated to unbiased financial tools and education.</li>
              <li><strong>Supports Multiple Scenarios:</strong> Explore and compare different fund performance assumptions or saving goals.</li>
              <li><strong>Accessible Anytime:</strong> Available 24/7 from mobile or desktop.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Whether you're just starting your investment journey or optimizing your existing SIPs, TheFinmaster’s PNB SIP Calculator is your go-to decision-making companion.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Which Factors Influence SIP Earnings?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              SIP earnings vary and are subject to multiple influencing factors. Understanding these helps you make more realistic and informed decisions.
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Key Factors That Impact SIP Returns:</strong>
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Investment Duration:</strong> Longer investments compound more, resulting in higher returns.</li>
              <li><strong>Monthly Contribution (SIP Amount):</strong> The more you invest monthly, the higher your potential corpus.</li>
              <li><strong>Expected Return Rate:</strong> Typically varies with fund type—debt, hybrid, or equity.</li>
              <li><strong>Fund Category:</strong> Equity funds are higher risk and potentially higher return, while debt funds are safer with lower returns.</li>
              <li><strong>Market Performance:</strong> Since SIPs are linked to mutual fund NAVs, market trends can impact returns.</li>
              <li><strong>Fund Manager Strategy:</strong> Active fund management quality plays a significant role in returns.</li>
              <li><strong>Expense Ratio:</strong> Lower management fees translate to better returns over time.</li>
              <li><strong>Inflation:</strong> Reduces the real value of your maturity amount—always account for inflation in long-term goals.</li>
              <li><strong>Consistency:</strong> Regular, uninterrupted SIPs perform better than erratic investments.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              By factoring in these elements, investors can better forecast realistic returns and choose funds that match their risk appetite.
            </p>
          </div>
        </div>

        <div className="w-full md:w-1/3 p-4">
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
              <h2 className="text-lg font-semibold ">Popular Categories</h2>
            </div>
            <div className="p-2 ">
              <ul>
                <Link to="/financial-tools-calculators/swp-calculator">
                  <li className="py-2  hover:bg-slate-100 border-b border-gray-300">
                    SWP Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/mutul-fund-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Mutual Fund Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/sukanya-samriddhi-yojana-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Samriddhi Yojana Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/fd-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    FD Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/rd-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    RD Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/nps-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    NPS Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/hra-exemption-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    HRA Exemption Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/retirement-planning-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Retirement Planning Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/emi-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    EMI Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/simple-interest-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Simple Interest Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/compound-interest-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Compound Interest Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/nsc-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    NSC Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/step-up-sip-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Step Up Sip Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/income-tax-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Income Tax Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/home-loan-emi-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Home Loan Emi Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/car-loan-emi-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Car Loan Emi Calculator
                  </li>
                </Link>
                {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
              </ul>
            </div>
          </div>
          <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
            <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
              <h2 className="text-lg font-semibold">More Categories</h2>
            </div>
            <div className="p-2">
              <ul>
                <Link to="/financial-tools-calculators/gratuity-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Gratuity Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/APY-calculator">
                  <li className="py-2 border- hover:bg-slate-100 border-gray-300">
                    APY Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/cagr-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    CAGR Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/gst-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    GST Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/flat-vs-reducing-rate-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Flat Vs Reducing Rate Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/equity-margin-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Equity Margin Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/inflation-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Inflation Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/salary-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Salary Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/brokerage-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Brokerage Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/tds-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    TDS Calculator
                  </li>
                </Link>
                {/* {moreCategories.map(category => (
          <Link key={category.id} to={`/category/${category.id}`}>
            <li className="py-2 border-b border-gray-300">{category.name}</li>
          </Link>
        ))} */}
              </ul>
            </div>
          </div>
        </div>
      </div>
      {/* FAQ section */}
      <div className="max-w-4xl mx-auto py-6 mt-10 px-2">
        <h1 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">
          Frequently Asked Questions
        </h1>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default PnbSipCalculator;
